import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Feather,
  LogOut,
  Download,
  Loader2,
  Mail,
  DollarSign,
  Music,
  Users,
  TrendingUp,
  Lightbulb,
  CheckCircle2,
  AlertTriangle,
  Info,
  BookOpen,
  Headphones,
} from "lucide-react";
import AdminReader from "@/components/AdminReader";
import { toast } from "sonner";
import {
  adminLogin,
  adminLogout,
  fetchMe,
  fetchNotify,
  fetchSales,
  fetchFreeDownloads,
  fetchReport,
  exportReport,
} from "@/lib/admin";

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await adminLogin(email, password);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 grain relative z-10">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center gap-3 justify-center mb-10">
          <Feather className="w-5 h-5 text-primary-gold" />
          <span className="font-serif-display text-2xl">The Leckie Novels</span>
        </Link>
        <div className="border border-white/10 bg-[#121212] p-8">
          <h1 className="font-serif-display text-3xl mb-2">Admin Login</h1>
          <p className="text-soft text-sm mb-8">Sign in to view your reports.</p>
          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-primary-gold transition-colors">
              <Mail className="w-4 h-4 text-soft" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                data-testid="admin-email-input"
                className="w-full bg-transparent py-3 text-text-primary placeholder:text-soft/60 focus:outline-none text-sm"
              />
            </div>
            <div className="border-b border-white/20 focus-within:border-primary-gold transition-colors">
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="admin-password-input"
                className="w-full bg-transparent py-3 text-text-primary placeholder:text-soft/60 focus:outline-none text-sm"
              />
            </div>
            {error && (
              <p className="text-[#d6453a] text-sm font-mono-ui" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="btn-gold w-full py-3.5 flex items-center justify-center gap-2 font-mono-ui text-xs uppercase tracking-[0.2em]"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "insights", label: "Insights", icon: Lightbulb },
  { id: "sales", label: "Sales", icon: DollarSign },
  { id: "notify", label: "Dingo List", icon: Users },
  { id: "free", label: "Free Song", icon: Music },
  { id: "reader", label: "E-Reader", icon: BookOpen },
];

const ADVICE_STYLES = {
  success: { icon: CheckCircle2, color: "#4ade80", border: "rgba(74,222,128,0.3)", bg: "rgba(74,222,128,0.06)" },
  warning: { icon: AlertTriangle, color: "#e0982f", border: "rgba(224,152,47,0.3)", bg: "rgba(224,152,47,0.06)" },
  info: { icon: Info, color: "#2ec5d6", border: "rgba(46,197,214,0.3)", bg: "rgba(46,197,214,0.06)" },
};

const SALES_HEADERS = ["Date", "Product", "Amount", "Status"];
const NOTIFY_HEADERS = ["Email", "Book", "Signed Up"];
const FREE_HEADERS = ["Date", "Song", "IP"];

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-white/10 bg-[#121212] p-6">
      <Icon className="w-5 h-5 text-primary-gold" />
      <p className="text-soft font-mono-ui text-xs uppercase tracking-[0.2em] mt-4">{label}</p>
      <p className="font-serif-display text-3xl mt-1">{value}</p>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("insights");
  const [sales, setSales] = useState(null);
  const [notify, setNotify] = useState(null);
  const [free, setFree] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, n, f, r] = await Promise.all([
          fetchSales(),
          fetchNotify(),
          fetchFreeDownloads(),
          fetchReport(),
        ]);
        setSales(s);
        setNotify(n);
        setFree(f);
        setReport(r);
      } catch (e) {
        toast.error("Could not load reports.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleExport = async (report, filename) => {
    try {
      await exportReport(report, filename);
      toast.success("Export downloaded.");
    } catch (e) {
      toast.error("Export failed.");
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div className="min-h-screen relative z-10 grain">
      <header className="border-b border-white/10 backdrop-blur-xl bg-[#0B0C10]/70 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-3">
          <Link to="/" data-testid="admin-header-logo" className="flex items-center gap-3 min-w-0">
            <Feather className="w-5 h-5 text-primary-gold shrink-0" />
            <span className="font-serif-display text-xl truncate"><span className="hidden sm:inline">Admin · </span>The Leckie Novels</span>
          </Link>
          <div className="flex items-center gap-4 sm:gap-6 shrink-0">
            <Link to="/" data-testid="admin-header-site" className="hidden sm:block font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold">View site</Link>
            <Link to="/admin/books" data-testid="admin-header-review-books" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold hover:opacity-80"><Headphones className="w-4 h-4" /><span className="hidden sm:inline">Review books</span></Link>
          <button
            onClick={onLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold transition-colors"
          >
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Log out</span>
          </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-32">
            <Loader2 className="w-8 h-8 text-primary-gold animate-spin" />
          </div>
        ) : (
          <>
            {/* stat overview */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
              <StatCard
                icon={DollarSign}
                label="Revenue"
                value={`$${sales?.summary.total_revenue?.toFixed(2) ?? "0.00"}`}
              />
              <StatCard icon={TrendingUp} label="Paid Orders" value={sales?.summary.paid_orders ?? 0} />
              <StatCard icon={Users} label="Dingo Signups" value={notify?.count ?? 0} />
              <StatCard icon={Music} label="Free Downloads" value={free?.count ?? 0} />
            </div>

            {/* tabs */}
            <div className="flex gap-2 border-b border-white/10 mb-8 overflow-x-auto whitespace-nowrap">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`shrink-0 flex items-center gap-2 px-4 sm:px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors ${
                    tab === t.id
                      ? "border-primary-gold text-primary-gold"
                      : "border-transparent text-soft hover:text-text-primary"
                  }`}
                >
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>

            {/* INSIGHTS */}
            {tab === "insights" && report && (
              <div data-testid="admin-insights-panel">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-serif-display text-2xl">Download Report &amp; Advice</h2>
                    <p className="text-soft text-xs font-mono-ui mt-1">
                      Generated {fmtDate(report.generated_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleExport("report", "downloads-report.csv")}
                    data-testid="export-report-button"
                    className="btn-ghost px-5 py-2.5 flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em]"
                  >
                    <Download className="w-4 h-4" /> Export CSV
                  </button>
                </div>

                {/* trend chips */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <StatCard icon={DollarSign} label="Paid (7d)" value={report.trends.paid_last_7} />
                  <StatCard icon={TrendingUp} label="Paid (30d)" value={report.trends.paid_last_30} />
                  <StatCard icon={Music} label="Free (7d)" value={report.trends.free_last_7} />
                  <StatCard icon={Music} label="Free (30d)" value={report.trends.free_last_30} />
                </div>

                {/* per-product free vs paid */}
                <h3 className="font-serif-display text-xl mb-4">Downloads by title</h3>
                <div className="border border-white/10 overflow-x-auto mb-10" data-testid="report-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 bg-[#121212]">
                        {["Title", "Type", "Purchased", "Revenue", "Free"].map((h) => (
                          <th key={h} className="text-left font-mono-ui text-xs uppercase tracking-[0.18em] text-soft px-5 py-4">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {report.by_product.map((p) => (
                        <tr key={p.product_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className="px-5 py-4 text-text-primary">{p.title}</td>
                          <td className="px-5 py-4 text-soft capitalize">{p.type}</td>
                          <td className="px-5 py-4 text-text-primary">{p.paid_units}</td>
                          <td className="px-5 py-4 text-primary-gold">${p.revenue.toFixed(2)}</td>
                          <td className="px-5 py-4 text-[#2ec5d6]">{p.free_downloads}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* advice */}
                <h3 className="font-serif-display text-xl mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary-gold" /> Recommendations
                </h3>
                <div className="space-y-3" data-testid="advice-list">
                  {report.advice.map((a, i) => {
                    const s = ADVICE_STYLES[a.type] || ADVICE_STYLES.info;
                    const Icon = s.icon;
                    return (
                      <div
                        key={`${a.type}-${i}`}
                        className="flex items-start gap-3 border p-4"
                        style={{ borderColor: s.border, background: s.bg }}
                      >
                        <Icon className="w-5 h-5 shrink-0 mt-0.5" style={{ color: s.color }} />
                        <p className="text-sm text-text-primary/90 leading-relaxed">{a.text}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SALES */}
            {tab === "sales" && (
              <div data-testid="admin-sales-panel">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif-display text-2xl">Sales Report</h2>
                  <button
                    onClick={() => handleExport("sales", "sales-report.csv")}
                    data-testid="export-sales-button"
                    className="btn-ghost px-5 py-2.5 flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em]"
                  >
                    <Download className="w-4 h-4" /> Export CSV
                  </button>
                </div>

                {sales?.by_product?.length > 0 && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    {sales.by_product.map((p) => (
                      <div key={p.product_id} className="border border-white/10 bg-[#121212] p-5">
                        <p className="font-serif-display text-lg">{p.product_title}</p>
                        <p className="text-soft font-mono-ui text-xs mt-2">
                          {p.units} sold · ${p.revenue.toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                <Table
                  headers={SALES_HEADERS}
                  rows={(sales?.transactions || []).map((t) => [
                    fmtDate(t.created_at),
                    t.product_title,
                    `$${Number(t.amount).toFixed(2)}`,
                    t.payment_status,
                  ])}
                  empty="No transactions yet."
                  testid="sales-table"
                />
              </div>
            )}

            {/* NOTIFY */}
            {tab === "notify" && (
              <div data-testid="admin-notify-panel">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif-display text-2xl">Dingo Launch List</h2>
                  <button
                    onClick={() => handleExport("notify", "dingo-notify-list.csv")}
                    data-testid="export-notify-button"
                    className="btn-ghost px-5 py-2.5 flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em]"
                  >
                    <Download className="w-4 h-4" /> Export CSV
                  </button>
                </div>
                <Table
                  headers={NOTIFY_HEADERS}
                  rows={(notify?.signups || []).map((s) => [s.email, s.product_id, fmtDate(s.created_at)])}
                  empty="No signups yet."
                  testid="notify-table"
                />
              </div>
            )}

            {/* FREE */}
            {tab === "free" && (
              <div data-testid="admin-free-panel">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif-display text-2xl">Free Song Downloads</h2>
                  <button
                    onClick={() => handleExport("free-downloads", "free-song-downloads.csv")}
                    data-testid="export-free-button"
                    className="btn-ghost px-5 py-2.5 flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em]"
                  >
                    <Download className="w-4 h-4" /> Export CSV
                  </button>
                </div>
                <Table
                  headers={FREE_HEADERS}
                  rows={(free?.downloads || []).map((d) => [fmtDate(d.downloaded_at), d.product_title, d.ip || "—"])}
                  empty="No free downloads yet."
                  testid="free-table"
                />
              </div>
            )}

            {tab === "reader" && <AdminReader />}
          </>
        )}
      </div>
    </div>
  );
}

function Table({ headers, rows, empty, testid }) {
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10 bg-[#121212]">
            {headers.map((h) => (
              <th
                key={h}
                className="text-left font-mono-ui text-xs uppercase tracking-[0.18em] text-soft px-5 py-4"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="text-center text-soft py-12">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={`${r[0]}-${i}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                {r.map((c, j) => (
                  <td key={headers[j]} className="px-5 py-4 text-text-primary">
                    {c}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking

  useEffect(() => {
    fetchMe()
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  const logout = async () => {
    await adminLogout();
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-gold animate-spin" />
      </div>
    );
  }

  return authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />;
}
